import CheckIcon from '@mui/icons-material/Check';
import { Box, Typography } from '@mui/material';
import { WorkData } from 'assets/config';
import CSIcon1 from 'assets/img/caseStudy/CS-icon-1.png';
import DiscussProjectSection from 'components/CaseStudy/elements/DiscussProjectSection';
import { generateCaseStudySchema, generateOrganizationSchema } from 'components/CaseStudy/SEOUtils';
import { E_COMMERCE_STYLES } from 'components/ECommerce/config';
import FeaturedCaseStudies from 'components/mobileLanding/FeaturedCaseStudies';
import VideoPlayer from 'components/VideoPlayer';
import useDevice from 'hooks/useDevice';
import { Suspense, useMemo } from 'react';
import { lazy } from 'react';
import { Helmet } from 'react-helmet';

import Cover from 'assets/img/caseStudy/Focus/Focus-motors-showroom-cover-photo.jpg';
import OsperbGrid from 'assets/img/caseStudy/Focus/osperb-grid-for-focus-image-.jpg';
import { FigureWithCaption, BulletList, SectionDescription, SectionHeading, SectionTitle, LargeHeading, SectionBox, SectionMediumDescription } from 'components/CaseStudy/CaseStudyUtils';

// Images from focusShowroomData.ts
import OsperbWebsite from 'assets/img/caseStudy/Focus/Focus-motors-website-developed-by-osperb.jpg';
import OsperbInspectionWeb from 'assets/img/caseStudy/Focus/Osperb-grid-for-focus-inspection-web.jpg';

// Images and videos from focusProductsData.ts
import FocusWebAllMocUp from 'assets/img/caseStudy/Focus/focus-motors-web-all-mocup.jpg'
import FocusCrmApp from 'assets/img/caseStudy/Focus/Focus-crm-app.mp4'
import InspectionBooking from 'assets/img/caseStudy/Focus/inspection-booking.mp4'
import FocusCarImage from 'assets/img/caseStudy/Focus/focus-motors-car-image.jpg'
import notePadWeb from 'assets/img/caseStudy/Focus/notePadWeb.webp'



const DefaultNavbar = lazy(() => import('../../components/DefaultNavbar'));

const CSFocus = () => {
  const { screenWidth, isMobile } = useDevice();
  const PreLoad = (
    <div className="h-screen w-100 flex items-center justify-center bg-black">
      {/* <h3 className="text-osperb-blue">Osperb</h3> */}
    </div>
  );
  const featuredProjects = useMemo(() => WorkData.slice(0, 3), []);

  const caseStudySchema = useMemo(() => {
    return generateCaseStudySchema({
      title: 'Custom-Coded Used Car Showroom Software | Focus Motors x Osperb',
      description:
        'Explor how Osperb systemised Focus Motors with a custom tech ecosystem: conversion-focused virtual showroom, custom CRM, sales app, role-based dashboards, inspection workflows, WhatsApp automation, and multi-branch management. built for scalable operations.',
      image: FocusWebAllMocUp,
      author: 'Osperb',
      organization: 'Osperb',
    });
  }, []);

  const organizationSchema = useMemo(() => generateOrganizationSchema(), []);

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(caseStudySchema)}</script>
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      </Helmet>

      <Suspense fallback={PreLoad}>
        <DefaultNavbar />
      </Suspense>

      <FigureWithCaption
        src={Cover}
        alt="Focus Motors digital transformation case study - Used car showroom technology ecosystem implementation by Osperb showcasing 5x revenue growth through automated lead management and inspection systems"
        title="Focus Motors Case Study: Tech-Driven Used Car Business Transformation"
        caption="Focus Motors Case Study: Tech-Driven Used Car Business Transformation"
        mt="0px"
      />
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          pt: 0,
          px: { xs: 2, sm: 3, md: 4 },
        }}
        maxWidth={E_COMMERCE_STYLES.container.maxWidthTabletLg}
        margin={E_COMMERCE_STYLES.container.mx}
      >
        <Box sx={{ mt: 0 }}>
          <Typography
            className="font-satoshi-regular"
            sx={{
              fontSize: { xs: 28, sm: 36, md: 48 },
              lineHeight: 1.1,
            }}
          >
            How we helped Focus Motors achieve 5× revenue by transforming a used-car showroom into a
            connected Tech Ecosystem
          </Typography>
          <Typography
            className="font-satoshi-regular"
            sx={{
              fontSize: { xs: 18, sm: 20, md: 24 },
              lineHeight: 1.1,
              mt: { xs: '24px', md: '40px' },
            }}
          >
            Leading pre-owned car business Focus Motors needed faster lead handling, better
            follow-ups, and more customer trust to grow.
          </Typography>
          <Typography
            className="font-satoshi-regular"
            sx={{
              fontSize: '18px',
              lineHeight: '150%',
              mt: { xs: '40px', md: '72px' },
            }}
          >
            We transformed their operations into a connected Tech Ecosystem delivered
            phase-by-phase. The impact was immediate: lead response dropped from ~48 hours to
            minutes, follow-ups became trackable alerts, inspections scaled 5×, and two branches
            with 32+ staff began operating with consistent processes and real-time visibility -
            supporting Focus Motors’ 5× revenue growth.
          </Typography>

          {/* Video Player Section */}

          <Box sx={{ mt: { xs: '40px', md: '72px' }, mb: { xs: '24px', md: '40px' } }}>
            <VideoPlayer
              src={'https://www.youtube.com/watch?v=7Ubw9QgZw3g'}
              autoPlay={false}
              loop={false}
              muted={true}
              controls={true}
            />
          </Box>

          <Box sx={{ mt: { xs: '50px', md: '100px' } }}>
            <Typography
              className="font-satoshi-bold"
              sx={{
                fontSize: { xs: 14, md: 16 },
                lineHeight: 1.1,
                color: '#00000080',
              }}
            >
              The challenge
            </Typography>
            <Typography
              className="font-satoshi-regular"
              sx={{
                fontSize: { xs: 22, sm: 28, md: 32 },
                mt: '12px',
                lineHeight: '140%',
              }}
            >
              In used-car business,
              <br />
              customers decide fast, and they decide based on <b>trust</b>. Focus Motors had
              multiple business motions happening at once:
            </Typography>

            {/* Business Motions List */}
            <Box sx={{ mt: { xs: '20px', md: '31px' }, ml: { xs: '0', md: '5px' } }}>
              <ul
                style={{
                  listStyleType: 'disc',
                  paddingLeft: '20px',
                  margin: 0,
                }}
              >
                <li>
                  <Typography
                    className="font-satoshi-regular"
                    sx={{
                      fontSize: { xs: 16, md: 18 },
                      lineHeight: '160%',
                      mb: '12px',
                    }}
                  >
                    Customers browsing cars and sending enquiries
                  </Typography>
                </li>
                <li>
                  <Typography
                    className="font-satoshi-regular"
                    sx={{
                      fontSize: { xs: 16, md: 18 },
                      lineHeight: '160%',
                      mb: '12px',
                    }}
                  >
                    Sales teams handling follow-ups (often on the move)
                  </Typography>
                </li>
                <li>
                  <Typography
                    className="font-satoshi-regular"
                    sx={{
                      fontSize: { xs: 16, md: 18 },
                      lineHeight: '160%',
                      mb: '12px',
                    }}
                  >
                    Inventory and vehicle data changing daily
                  </Typography>
                </li>
                <li>
                  <Typography
                    className="font-satoshi-regular"
                    sx={{
                      fontSize: { xs: 16, md: 18 },
                      lineHeight: '160%',
                      mb: '12px',
                    }}
                  >
                    Inspections needing credibility and proof
                  </Typography>
                </li>
                <li>
                  <Typography
                    className="font-satoshi-regular"
                    sx={{
                      fontSize: { xs: 16, md: 18 },
                      lineHeight: '160%',
                    }}
                  >
                    {`Branch coordination needing consistency (not "each branch doing its own thing")`}
                  </Typography>
                </li>
              </ul>
            </Box>

            {/* Pain Points Section */}
            <Box sx={{ mt: { xs: '50px', md: '100px' } }}>
              <Typography
                className="font-satoshi-bold"
                sx={{
                  fontSize: { xs: 14, md: 16 },
                  lineHeight: 1.5,
                  color: '#00000080',
                  mb: '12px',
                }}
              >
                The pain looked like this
              </Typography>

              {/* Pain Point 1 */}
              <Box
                sx={{ display: 'flex', alignItems: 'flex-start', mb: { xs: '16px', md: '24px' } }}
              >
                <Box
                  sx={{
                    width: '4px',
                    height: { xs: '20px', md: '24px' },
                    bgcolor: '#FF9966',
                    borderRadius: '0px',
                    mx: { xs: '8px', md: '12px' },
                    mr: { xs: '12px', md: '20px' },
                    mt: '4px',
                    flexShrink: 0,
                    transform: 'rotate(90deg)',
                  }}
                />
                <Typography
                  className="font-satoshi-regular"
                  sx={{
                    fontSize: { xs: 16, md: 18 },
                    lineHeight: '160%',
                  }}
                >
                  <b>Slow initial response</b> → lead leaks
                </Typography>
              </Box>

              {/* Pain Point 2 */}
              <Box
                sx={{ display: 'flex', alignItems: 'flex-start', mb: { xs: '16px', md: '24px' } }}
              >
                <Box
                  sx={{
                    width: '4px',
                    height: { xs: '20px', md: '24px' },
                    bgcolor: '#FF9966',
                    borderRadius: '0px',
                    mx: { xs: '8px', md: '12px' },
                    mr: { xs: '12px', md: '20px' },
                    mt: '4px',
                    flexShrink: 0,
                    transform: 'rotate(90deg)',
                  }}
                />
                <Typography
                  className="font-satoshi-regular"
                  sx={{
                    fontSize: { xs: 16, md: 18 },
                    lineHeight: '160%',
                  }}
                >
                  <b>Manual follow-up discipline</b> → missed opportunities
                </Typography>
              </Box>

              {/* Pain Point 3 */}
              <Box
                sx={{ display: 'flex', alignItems: 'flex-start', mb: { xs: '16px', md: '24px' } }}
              >
                <Box
                  sx={{
                    width: '4px',
                    height: { xs: '20px', md: '24px' },
                    bgcolor: '#FF9966',
                    borderRadius: '0px',
                    mx: { xs: '8px', md: '12px' },
                    mr: { xs: '12px', md: '20px' },
                    mt: '4px',
                    flexShrink: 0,
                    transform: 'rotate(90deg)',
                  }}
                />
                <Typography
                  className="font-satoshi-regular"
                  sx={{
                    fontSize: { xs: 16, md: 18 },
                    lineHeight: '160%',
                  }}
                >
                  <b>Weak visibility for leadership</b> → decisions based on partial info
                </Typography>
              </Box>

              {/* Pain Point 4 */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    width: '4px',
                    height: { xs: '20px', md: '24px' },
                    bgcolor: '#FF9966',
                    borderRadius: '0px',
                    mx: { xs: '8px', md: '12px' },
                    mr: { xs: '12px', md: '20px' },
                    mt: '4px',
                    flexShrink: 0,
                    transform: 'rotate(90deg)',
                  }}
                />
                <Typography
                  className="font-satoshi-regular"
                  sx={{
                    fontSize: { xs: 16, md: 18 },
                    lineHeight: '160%',
                  }}
                >
                  <b>Inspection trust gap</b> → buyer doubt + potential disputes
                </Typography>
              </Box>

              <Box sx={{ mt: { xs: '40px', md: '72px' }, mb: { xs: '24px', md: '40px' } }}>
                <VideoPlayer
                  src={'https://www.youtube.com/shorts/IhedpEyxcsE'}
                  autoPlay={false}
                  loop={false}
                  muted={false}
                  isReel
                />
              </Box>

              <Box sx={{ my: '72px' }}>
                <Typography
                  className="font-satoshi-regular"
                  sx={{
                    fontSize: { xs: 14, md: 16 },
                    lineHeight: '140%',
                    mb: 2,
                  }}
                >
                  Solution
                </Typography>
                <Typography
                  className="font-satoshi-regular"
                  sx={{
                    fontSize: { xs: 26, sm: 32, md: 40 },
                    lineHeight: '140%',
                    mb: 2,
                  }}
                >
                  We Built an ecosystem, not just a single app.
                </Typography>
                <Typography>
                  We designed the solution as a
                  connected system with 5 pillars:
                </Typography>

                {/* 5 Pillars List */}
                <Box sx={{ mt: { xs: '24px', md: '20px' } }}>
                  <ol
                    style={{
                      listStyleType: 'decimal',
                      paddingLeft: '20px',
                      margin: 0,
                    }}
                  >
                    <li>
                      <Typography
                        className="font-satoshi-regular"
                        sx={{
                          fontSize: { xs: 16, md: 18 },
                          lineHeight: '200%',
                          mb: '16px',
                        }}
                      >
                        Virtual Showroom (Website) to convert interest into structured leads
                      </Typography>
                    </li>
                    <li>
                      <Typography
                        className="font-satoshi-regular"
                        sx={{
                          fontSize: { xs: 16, md: 18 },
                          lineHeight: '200%',
                          mb: '16px',
                        }}
                      >
                        CRM + Inventory + Operations CMS to centralize execution
                      </Typography>
                    </li>
                    <li>
                      <Typography
                        className="font-satoshi-regular"
                        sx={{
                          fontSize: { xs: 16, md: 18 },
                          lineHeight: '200%',
                          mb: '16px',
                        }}
                      >
                        Sales Mobile App to make follow-ups real-time and field-friendly
                      </Typography>
                    </li>
                    <li>
                      <Typography
                        className="font-satoshi-regular"
                        sx={{
                          fontSize: { xs: 16, md: 18 },
                          lineHeight: '200%',
                          mb: '16px',
                        }}
                      >
                        {`Focus Inspection to make trust "verifiable"`}
                      </Typography>
                    </li>
                    <li>
                      <Typography
                        className="font-satoshi-regular"
                        sx={{
                          fontSize: { xs: 16, md: 18 },
                          lineHeight: '200%',
                        }}
                      >
                        Multi-Branch Management to standardize operations and reporting
                      </Typography>
                    </li>
                  </ol>
                </Box>
              </Box>

              <Box>
                <VideoPlayer
                  src={'https://youtube.com/shorts/_8zcXKYI9nI'}
                  autoPlay={false}
                  loop={false}
                  muted={false}
                  isReel={true}
                />
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              mt: { xs: '50px', md: '150px' },
              maxWidth: { xs: '100%', md: '720px' },
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'start',
              justifyContent: 'space-between',
              gap: { xs: '16px', md: '10px' },
              mb: '-20px'
            }}
          >
            <img
              src={CSIcon1}
              alt="Focus Virtual Showroom"
              style={{
                width: '72.38px',
                height: '91.86px',
                transform: 'rotate(-11.9deg)',
                opacity: 1,
                marginTop: '-10px',
                flexShrink: 0,
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography
                className="font-satoshi-regular"
                sx={{
                  fontSize: { xs: 32, sm: 40, md: 48 },
                  lineHeight: '110%',
                  color: '#000000',
                }}
              >
                Focus Virtual Showroom
              </Typography>
              <Typography
                className="font-satoshi-regular"
                sx={{
                  mt: { xs: '20px', md: '32px' },
                  // mb: { xs: '32px', md: '57px' },
                  fontSize: { xs: 14, md: 16 },
                  lineHeight: '150%',
                }}
              >
                A responsive, fast browsing experience built with{' '}
                <b>React.js + Material UI + Redux Toolkit</b>, designed from customer psychology,
                “reduce confusion, increase clarity, trigger action”.
              </Typography>
            </Box>
          </Box>

          {/* Focus Virtual Showroom Features */}
          <SectionBox mt="100px">
            <Box sx={{ borderLeft: '3px solid #FF903587', pl: '5px' }}>
              <Typography
                className="font-satoshi-medium"
                sx={{ fontSize: 16, lineHeight: '110%', color: '#707070', mb: '12px' }}
              >
                Focus Virtual Showroom
              </Typography>
            </Box>
            <SectionHeading title="Inventory auto-sync (no manual updates)" marginTop={false} />
            <SectionDescription mt="24px">
              Every car available in the garage is automatically synced to the website inventory. So the website always shows <b>latest stock</b>, with accurate details and availability — without staff updating it manually.
            </SectionDescription>
          </SectionBox>

          <SectionBox mt="100px">
            <Box sx={{ borderLeft: '3px solid #FF903587', pl: '5px' }}>
              <Typography
                className="font-satoshi-medium"
                sx={{ fontSize: 16, lineHeight: '110%', color: '#707070', mb: '12px' }}
              >
                Focus Virtual Showroom
              </Typography>
            </Box>
            <SectionHeading title="Customer explores cars like a real showroom" marginTop={false} />
            <SectionDescription mt="24px" sx={{ mb: '12px' }}>Customers can:</SectionDescription>
            <BulletList
              items={[
                <>Filter cars by brand, model, year, price, fuel type</>,
                <>Open a car detail page and view photos, specs, videos, and EMI options <br /> The UI is planned with conversion in mind — <b>less confusion, faster decisions, clear CTAs.</b></>
              ]}
              mt="0px"
            />
            <FigureWithCaption
              src={OsperbGrid}
              alt="Focus Motors virtual showroom interface showing customer browsing experience with filters and car details"
              title="Focus Motors virtual showroom interface showing customer browsing experience with filters and car details"
              caption="Focus Motors virtual showroom interface showing customer browsing experience with filters and car details"
              mt="57px"
            />
          </SectionBox>

          <SectionBox mt="100px">
            <Box sx={{ borderLeft: '3px solid #FF903587', pl: '5px' }}>
              <Typography
                className="font-satoshi-medium"
                sx={{ fontSize: 16, lineHeight: '110%', color: '#707070', mb: '12px' }}
              >
                Focus Virtual Showroom
              </Typography>
            </Box>
            <SectionHeading title="Direct test drive booking (instant action)" marginTop={false} />
            <SectionDescription mt="24px">
              When a customer likes a car, they can <b>book a test drive directly from the website </b>. <br /> This converts "just browsing" into a real lead without depending on phone calls.
            </SectionDescription>
            <FigureWithCaption
              src={OsperbWebsite}
              alt="Focus Motors test drive booking interface showing instant action conversion"
              title="Focus Motors test drive booking interface showing instant action conversion"
              caption="Focus Motors test drive booking interface showing instant action conversion"
              mt="57px"
            />
          </SectionBox>

          <SectionBox mt="100px">
            <Box sx={{ borderLeft: '3px solid #FF903587', pl: '5px' }}>
              <Typography
                className="font-satoshi-medium"
                sx={{ fontSize: 16, lineHeight: '110%', color: '#707070', mb: '12px' }}
              >
                Focus Virtual Showroom
              </Typography>
            </Box>
            <SectionHeading title="Inspection booking with payment" marginTop={false} />
            <SectionDescription mt="24px" sx={{ mb: '12px' }}>
              Customers can book a vehicle inspection through the website. <br /> If needed, they can also <b>pay the inspection fee online while booking</b>, which:
            </SectionDescription>
            <BulletList
              items={[
                <>confirms serious intent</>,
                <>reduces cancellations</>,
                <>makes the process smoother for both customer and team</>
              ]}
              mt="0px"
            />

            <Box sx={{ margin: '0 auto' }}>
              <VideoPlayer
                src={InspectionBooking}
                autoPlay={true}
                loop={true}
                muted={true}
              />
            </Box>


          </SectionBox>

          <SectionBox mt="100px">
            <Box sx={{ borderLeft: '3px solid #FF903587', pl: '5px' }}>
              <Typography
                className="font-satoshi-medium"
                sx={{ fontSize: 16, lineHeight: '110%', color: '#707070', mb: '12px' }}
              >
                Focus Virtual Showroom
              </Typography>
            </Box>
            <SectionHeading title="Vehicle history report booking" marginTop={false} />
            <SectionDescription mt="24px">
              Customers can also book a <b>vehicle history report</b> from the same website.<br /> This removes doubt early and helps the customer feel more confident before finalizing.<br /> <br /> "<b>My Dream Car</b>” (capture demand even when stock is not available) <br /><br /> If a customer’s favorite car is not currently listed, they can submit details in <b>My Dream Car</b>:
            </SectionDescription>
            <BulletList
              items={[
                <>preferred brand/model</>,
                <>year range</>,
                <>budget</>,
                <>fuel type / requirements</>
              ]}
              mt="24px"
            />
            <SectionDescription mt="20px">
              Focus Motors then uses this as a ready demand list to source the car and contact the customer. So even when stock is missing, the website still generates valuable leads.
            </SectionDescription>
            <Box sx={{ mt: '57px' }}>
              <VideoPlayer
                src="https://youtube.com/shorts/S7ms8cHQVQ8?si=zC4vhtXLiFMkNXt8"
                autoPlay={false}
                loop={false}
                muted={false}
                controls={true}
                isReel
              />
            </Box>
          </SectionBox>

          {/* Focus Products Section */}
          <SectionBox mt="100px">
            <Typography
              className='font-satoshi-regular'
              sx={{ fontSize: { xs: 32, sm: 40, md: 48 }, lineHeight: "110%", color: "#000000", mb: '24px' }}
            >
              Focus CRM + Inventory + Central CMS (HQ + Branch panels)
            </Typography>
            <SectionDescription mt="0px" sx={{ mb: '57px' }}>
              The CMS became the operating engine: every lead, vehicle, inspection, and task moves through a structured system. <br /><br />
              <div style={{ color: '#00000080', fontWeight: '700' }}>Core modules</div>
              <ul style={{ listStyleType: 'disc', paddingLeft: '20px', margin: 0, marginTop: '16px', marginLeft: '10px' }}>
                <li style={{ fontSize: 18, lineHeight: '150%', marginBottom: '12px' }}>Leads, Sales, Inspections, Vehicles, Staff, Settings</li>
                <li style={{ fontSize: 18, lineHeight: '150%' }}>Analytics dashboards for quicker monitoring and decisions</li>
              </ul>
            </SectionDescription>
            <FigureWithCaption
              src={FocusWebAllMocUp}
              alt="Focus CRM and Inventory Management System showing centralized operations dashboard"
              title="Focus CRM + Inventory + Central CMS (HQ + Branch panels)"
              caption="Focus CRM and Inventory Management System showing centralized operations dashboard"
              mt="0px"
            />
          </SectionBox>

          <SectionBox mt="100px">
            <Typography
              className='font-satoshi-regular'
              sx={{ fontSize: { xs: 32, sm: 40, md: 48 }, lineHeight: "110%", color: "#000000", mb: '24px' }}
            >
              Focus Sales Mobile App <br /> (simple CRM for field teams)
            </Typography>
            <SectionDescription mt="0px" sx={{ mb: '57px' }}>
              Built for speed and simplicity — so staff can execute follow-ups from anywhere. <br /> <br />
              <div style={{ color: '#00000080', fontWeight: '700' }}>Modules</div>
              <ul style={{ listStyleType: 'disc', paddingLeft: '20px', margin: 0, marginTop: '16px', marginLeft: '10px' }}>
                <li style={{ fontSize: 18, lineHeight: '150%', marginBottom: '12px' }}>Client, Vehicle, Lead, Task management</li>
                <li style={{ fontSize: 18, lineHeight: '150%', marginBottom: '12px' }}>Real-time lead status updates</li>
                <li style={{ fontSize: 18, lineHeight: '150%' }}>Secure access using permissions (role-based)</li>
              </ul>
            </SectionDescription>
            <Box sx={{ width: '300px', height: '530px', margin: '0 auto' }}>
              <VideoPlayer
                src={FocusCrmApp}
                autoPlay={true}
                loop={true}
                muted={true}
                isReel={true}
              />
            </Box>
          </SectionBox>

          <SectionBox mt="100px">
            <Typography
              className='font-satoshi-regular'
              sx={{ fontSize: { xs: 32, sm: 40, md: 48 }, lineHeight: "110%", color: "#000000", mb: '24px' }}
            >
              <div style={{ fontFamily: 'Satoshi', fontWeight: 400, fontSize: 24, lineHeight: "140%", color: '#000000' }}>Follow-up discipline system</div>
            </Typography>
            <SectionDescription mt="0px" sx={{ mb: '57px' }}>
              <ul style={{ listStyleType: 'disc', paddingLeft: '20px', margin: 0, marginTop: '16px', marginLeft: '10px' }}>
                <li style={{ fontSize: 18, lineHeight: '150%' }}>If a new lead is not followed up within <b>24 hours</b>, the <b>GM gets an escalation notification</b>.<br />This created accountability without micromanagement — the system itself protects sales performance.</li>
              </ul>
            </SectionDescription>
            <FigureWithCaption
              src={notePadWeb}
              alt="Focus Motors inspection booking interface with online payment option"
              title="Focus Motors inspection booking interface with online payment option"
              caption="Focus Motors inspection booking interface with online payment option"
              mt="57px"
            />
          </SectionBox>

          <SectionBox mt="100px">
            <Typography
              className='font-satoshi-regular'
              sx={{ fontSize: { xs: 32, sm: 40, md: 48 }, lineHeight: "110%", color: "#000000", mb: '24px' }}
            >
              Focus Inspection: trust that customers can verify
            </Typography>
            <SectionDescription mt="0px" sx={{ mb: '57px' }}>
              Focus Inspection was designed to remove doubt and reduce post-purchase disputes by making the inspection proof <b>tamper-resistant</b>.<br /> <br />
              <div style={{ color: '#00000080', fontWeight: '700' }}>What customers get</div>
              <ul style={{ listStyleType: 'disc', paddingLeft: '20px', margin: 0, marginTop: '16px', marginLeft: '10px' }}>
                <li style={{ fontSize: 18, lineHeight: '150%', marginBottom: '12px' }}>Online inspection booking (with or without payment)</li>
                <li style={{ fontSize: 18, lineHeight: '150%', marginBottom: '12px' }}>Automated WhatsApp updates</li>
                <li style={{ fontSize: 18, lineHeight: '150%', marginBottom: '12px' }}>130+ checkpoint inspection reports</li>
                <li style={{ fontSize: 18, lineHeight: '150%' }}>QR-verifiable reports stored online — anyone can verify by scanning the QR code.</li>
              </ul>
              <p style={{ marginTop: '20px', marginBottom: 0 }}>This trust layer contributed directly to adoption and scale:</p>
            </SectionDescription>
            <FigureWithCaption
              src={OsperbInspectionWeb}
              alt="Focus Motors inspection booking interface with online payment option"
              title="Focus Motors inspection booking interface with online payment option"
              caption="Focus Motors inspection booking interface with online payment option"
              mt="57px"
            />
            <Box sx={{ mt: { xs: "50px", md: "100px" }, mr: { xs: 0, md: '100px' }, ml: { xs: 0, md: '40px' }, maxWidth: { xs: '100%', md: "500px" } }}>
              <Typography className='font-satoshi-regular' sx={{ borderLeft: '2px solid #0000003D', pl: "24px", fontSize: { xs: "24px", md: "32px" }, lineHeight: "140%" }}>
                Inspection operations grew 5×, and the business expanded inspections to multiple regions (with new inspection staff added).
              </Typography>
            </Box>
            <Box sx={{ mt: "50px" }}>
              <VideoPlayer
                src="https://www.youtube.com/shorts/7bX-UAxIkVE"
                autoPlay={false}
                loop={false}
                muted={false}
                isReel={true}
              />
            </Box>
          </SectionBox>

          <SectionBox mt="100px">
            <Typography
              className='font-satoshi-regular'
              sx={{ fontSize: { xs: 32, sm: 40, md: 48 }, lineHeight: "110%", color: "#000000", mb: '24px' }}
            >
              Multi-Branch Management
            </Typography>
            <SectionDescription mt="0px" sx={{ mb: '57px' }}>
              With 2 branches actively using the system (Pandikkad + Kollam locations, the ecosystem standardizes operations across branches while giving HQ full visibility.
            </SectionDescription>
            <FigureWithCaption
              src={FocusCarImage}
              alt="Multi-Branch Management system showing standardized operations across locations"
              title="Multi-Branch Management"
              caption="Multi-Branch Management system showing standardized operations across locations"
              mt="0px"
            />
          </SectionBox>

          <SectionBox mt="100px">
            <Typography
              className='font-satoshi-regular'
              sx={{ fontSize: { xs: 32, sm: 40, md: 48 }, lineHeight: "110%", color: "#000000", mb: '24px' }}
            >
              Role-based dashboards
            </Typography>
            <SectionDescription mt="0px" sx={{ mb: '57px' }}>
              Each role has a focused dashboard and permissions — so people see what they need, act faster, and data stays secure. <br /> <br />

              <div style={{ color: '#00000080', fontWeight: '700' }}>What customers get</div>
              <ul style={{ listStyleType: 'disc', paddingLeft: '20px', margin: 0, marginTop: '16px', marginLeft: '10px' }}>
                <li style={{ fontSize: 18, lineHeight: '150%', marginBottom: '12px' }}>Online inspection booking (with or without payment)</li>
                <li style={{ fontSize: 18, lineHeight: '150%', marginBottom: '12px' }}>Automated WhatsApp updates</li>
                <li style={{ fontSize: 18, lineHeight: '150%', marginBottom: '12px' }}>130+ checkpoint inspection reports</li>
                <li style={{ fontSize: 18, lineHeight: '150%' }}>QR-verifiable reports stored online — anyone can verify by scanning the QR code.</li>
              </ul>
              <p style={{ marginTop: '20px', marginBottom: 0 }}>This trust layer contributed directly to adoption and scale:</p>
            </SectionDescription>
          </SectionBox>

          {/* Result and Impact Section */}
          <Box sx={{ mt: { xs: '50px', md: '100px' } }}>
            <Typography
              className="font-satoshi-regular"
              sx={{
                fontSize: { xs: 32, sm: 40, md: 48 },
                lineHeight: '110%',
                mb: { xs: '32px', md: '48px' },
                color: '#000',
              }}
            >
              Results & impact
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: '24px', md: '40px' },
                mb: { xs: '50px', md: '100px' },
              }}
            >
              {[
                {
                  title: 'Lead response speed',
                  stat: '~48 hours → minutes',
                  desc: 'Powered by: automated WhatsApp reply + structured lead routing + real-time staff execution.',
                },
                {
                  title: 'Inspection scale',
                  stat: '5× increase in inspections',
                  desc: 'Supported by: online booking + cloud report delivery + QR verification trust layer.',
                },
                {
                  title: 'Stronger follow-up discipline (without micromanagement)',
                  stat: '24-hour no-follow-up → GM escalation notification',
                  desc: 'Result: fewer missed leads, better accountability culture.',
                },
                {
                  title: 'Operational clarity across branches',
                  stat: 'Standardized workflows + role dashboards + centralized visibility',
                  desc: 'Result: faster decisions, less manual tracking, fewer internal “status confusion” calls.',
                },
              ].map((item, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Box
                    sx={{
                      minWidth: { xs: '20px', md: '24px' },
                      height: { xs: '20px', md: '24px' },
                      bgcolor: '#22C55E', // Vivid green
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: { xs: '12px', md: '16px' },
                      mt: '4px',
                    }}
                  >
                    <CheckIcon sx={{ color: 'white', fontSize: { xs: '16px', md: '18px' } }} />
                  </Box>
                  <Box>
                    <Typography
                      className="font-satoshi-regular"
                      sx={{
                        fontSize: { xs: 16, md: 18 },
                        color: '#000000',
                        marginBottom: '6px',
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      className="font-satoshi-bold"
                      sx={{
                        fontSize: { xs: 18, md: 20 },
                        color: '#000000',
                        marginBottom: '6px',
                      }}
                    >
                      {item.stat}
                    </Typography>
                    <Typography
                      className="font-satoshi-regular"
                      sx={{
                        fontSize: { xs: 14, md: 16 },
                        color: '#000000',
                        lineHeight: '1.5',
                      }}
                    >
                      {item.desc}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
      <DiscussProjectSection type={screenWidth > 768 ? 'web' : 'mobile'} />
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          pt: 10,
          px: 2,
        }}
        maxWidth={E_COMMERCE_STYLES.container.maxWidthWeb}
        margin={E_COMMERCE_STYLES.container.mx}
      >
        <FeaturedCaseStudies
          hideShadow={true}
          isSlider={isMobile ? false : true}
          type={isMobile ? 'mobile' : 'web'}
          projects={featuredProjects}
        />
      </Box>
    </>
  );
};

export default CSFocus;
